import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User as UserIcon, ArrowRight, Globe, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [preferredLang, setPreferredLang] = useState(
    (typeof navigator !== 'undefined' && navigator.language && navigator.language.toLowerCase().startsWith('en')) ? 'en' : 'it'
  );
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        if (!acceptTerms) {
          setError('Devi accettare i Termini e Condizioni per registrarti');
          setLoading(false);
          return;
        }
        if (name.includes('@')) {
          setError('Il nome non può contenere un indirizzo email');
          setLoading(false);
          return;
        }
        await register(email, password, name, preferredLang);
      } else {
        await login(email, password);
      }
      navigate(redirectTo);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg('Se questo indirizzo è registrato, riceverai a breve una email con il link per reimpostare la password.');
    } catch {
      setForgotMsg('Errore. Riprova fra poco.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] pt-20 px-4" data-testid="login-page">
      {showForgot && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={() => setShowForgot(false)} data-testid="forgot-overlay">
          <div className="bg-zinc-900 border border-zinc-800 w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Reimposta password</h2>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">Inserisci la tua email. Se l'account esiste, ti invieremo un link per creare una nuova password (valido 1 ora).</p>
            <form onSubmit={submitForgot} className="space-y-4">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="tua@email.com" className="pl-10 bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 h-10" required data-testid="forgot-email" />
              </div>
              {forgotMsg && <p className="text-xs text-emerald-400" data-testid="forgot-msg">{forgotMsg}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowForgot(false)} className="flex-1 border border-zinc-700 text-zinc-400 py-2 text-xs hover:border-zinc-500 transition-colors" data-testid="forgot-cancel">Annulla</button>
                <button type="submit" disabled={forgotLoading} className="flex-1 bg-[#C5A059] text-black py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-50" data-testid="forgot-submit">
                  {forgotLoading ? 'Invio...' : 'Invia link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {isRegister ? 'Crea Account' : 'Bentornato'}
          </h1>
          <p className="text-zinc-500 text-sm">
            {isRegister ? 'Registrati per pubblicare annunci' : 'Accedi al tuo account AeroDeals'}
          </p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 p-8">
          {/* Google Auth */}
          <button
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white text-zinc-900 py-3 text-sm font-medium hover:bg-zinc-100 transition-colors mb-6"
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continua con Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-600 uppercase">oppure</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Nome</Label>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <Input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Il tuo nome"
                    className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            {isRegister && (
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Lingua preferita / Preferred language</Label>
                <div className="relative">
                  <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none z-10" />
                  <select
                    value={preferredLang}
                    onChange={e => setPreferredLang(e.target.value)}
                    className="w-full pl-10 pr-8 bg-zinc-900 border border-zinc-800 text-white h-11 text-sm appearance-none cursor-pointer focus:outline-none focus:border-[#C5A059]"
                    data-testid="register-lang-select"
                  >
                    <option value="it">Italiano</option>
                    <option value="en">English</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none">▾</div>
                </div>
                <p className="text-[10px] text-zinc-600 mt-1.5">
                  Riceverai le email di AeroDeals in questa lingua.
                </p>
              </div>
            )}

            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="nome@email.com"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="La tua password"
                  className="pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#C5A059] transition-colors"
                  title={showPassword ? 'Nascondi password' : 'Mostra password'}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <p className="text-red-400 text-xs" data-testid="login-error">{error}</p>}

            {!isRegister && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotMsg(''); }}
                  className="text-[11px] text-zinc-500 hover:text-[#C5A059] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </button>
              </div>
            )}

            {isRegister && (
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={e => setAcceptTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#C5A059] bg-zinc-900 border-zinc-700 rounded cursor-pointer"
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="accept-terms" className="text-xs text-zinc-400 cursor-pointer leading-relaxed">
                  Accetto i <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline">Termini e Condizioni</a> è la <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline">Privacy Policy</a> di AeroDeals
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? 'Caricamento...' : (isRegister ? 'Registrati' : 'Accedi')}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            {isRegister ? 'Hai già un account?' : 'Non hai un account?'}
            <button
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-[#C5A059] ml-1 hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Accedi' : 'Registrati'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
